import { useState, useEffect } from "react";
import { Heart, MapPin, Calendar, Quote } from "lucide-react";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : '/api';

const BASE = process.env.REACT_APP_BACKEND_URL || '';
const resolveUrl = (url) => url?.startsWith('/api/uploads/') ? `${BASE}${url}` : url;

function AlumniCard({ kitten, onClick }) {
  return (
    <div 
      className="flex-shrink-0 w-80 bg-white rounded-3xl overflow-hidden shadow-soft hover:-translate-y-1 transition-transform duration-300 cursor-pointer"
      onClick={onClick}
      data-testid={`alumni-card-${kitten.id}`}
    >
      {/* Image */}
      <div className="relative h-56 img-hover-zoom">
        <img
          src={resolveUrl(kitten.image_url)}
          alt={kitten.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute bottom-3 left-3 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full">
          <span className="font-body text-sm font-semibold text-forest">
            {kitten.name}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="p-5">
        {/* Info badges */}
        <div className="flex flex-wrap gap-2 mb-4">
          <span className="flex items-center gap-1 text-xs bg-sand/50 text-forest/80 px-2 py-1 rounded-full">
            <Calendar className="w-3 h-3" />
            {kitten.adoption_date}
          </span>
          {kitten.adopter_location && (
            <span className="flex items-center gap-1 text-xs bg-sand/50 text-forest/80 px-2 py-1 rounded-full">
              <MapPin className="w-3 h-3" />
              {kitten.adopter_location}
            </span>
          )}
        </div>

        {/* Color & Parents */}
        <p className="font-body text-xs text-autumn font-medium mb-2">
          {kitten.color} • {kitten.parents}
        </p>

        {/* Short comment preview */}
        <p className="font-body text-sm text-forest/70 line-clamp-2 mb-3">
          "{kitten.comment.substring(0, 80)}..."
        </p>

        {/* Adopter */}
        <div className="pt-3 border-t border-sand/30 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Heart className="w-4 h-4 text-autumn" />
            <span className="font-body text-sm font-medium text-forest">
              {kitten.adopter_name}
            </span>
          </div>
          <span className="text-xs text-autumn font-medium">Voir +</span>
        </div>
      </div>
    </div>
  );
}

function AlumniModal({ kitten, isOpen, onClose }) {
  if (!kitten) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl bg-cream border-sand p-0 overflow-hidden">
        <div className="grid md:grid-cols-2">
          {/* Image */}
          <div className="relative h-80 md:h-full min-h-[400px] bg-forest/5">
            <img
              src={resolveUrl(kitten.image_url)}
              alt={kitten.name}
              className="w-full h-full object-cover"
            />
          </div>

          {/* Info */}
          <div className="p-8">
            <DialogHeader>
              <DialogTitle className="font-heading text-3xl font-bold text-forest mb-2">
                {kitten.name}
              </DialogTitle>
            </DialogHeader>
            
            <div className="flex flex-wrap gap-2 mb-4">
              <span className="inline-block bg-autumn/10 text-autumn font-body font-semibold px-3 py-1 rounded-full text-sm">
                Adopté en {kitten.adoption_date}
              </span>
              <span className="inline-block bg-forest/10 text-forest font-body font-medium px-3 py-1 rounded-full text-sm">
                {kitten.color}
              </span>
            </div>

            <div className="mb-6">
              <p className="font-body text-xs text-forest/60 mb-1">Parents</p>
              <p className="font-body font-medium text-forest">{kitten.parents}</p>
            </div>

            {/* Adopter comment */}
            <div className="bg-sand/30 rounded-2xl p-5 mb-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-forest rounded-full flex items-center justify-center">
                  <span className="font-body font-semibold text-white text-sm">
                    {kitten.adopter_name.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase()}
                  </span>
                </div>
                <div>
                  <p className="font-body font-semibold text-forest">{kitten.adopter_name}</p>
                  <p className="font-body text-xs text-forest/60">{kitten.adopter_location}</p>
                </div>
              </div>
              <div className="relative">
                <Quote className="absolute -top-1 -left-1 w-5 h-5 text-autumn/30" />
                <p className="font-body text-sm text-forest/80 leading-relaxed pl-5 italic">
                  "{kitten.comment}"
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 text-forest/60">
              <Heart className="w-4 h-4 text-autumn fill-autumn" />
              <span className="font-body text-sm">Adopté avec amour</span>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function AlumniSection() {
  const [alumni, setAlumni] = useState([]);
  const [selectedKitten, setSelectedKitten] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAlumni = async () => {
      try {
        const response = await fetch(`${API}/alumni`);
        if (response.ok) {
          const data = await response.json();
          setAlumni(data);
        }
      } catch (error) {
        console.error("Error fetching alumni:", error);
      }
      setLoading(false);
    };
    fetchAlumni();
  }, []);

  return (
    <section
      id="alumni"
      data-testid="alumni-section"
      className="relative py-24 lg:py-32 bg-cream overflow-hidden"
    >
      {/* Decorative elements */}
      <div className="absolute top-10 right-10 text-6xl opacity-5 rotate-12">❤️</div>
      <div className="absolute bottom-10 left-10 text-6xl opacity-5 -rotate-12">🐾</div>

      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section header */}
        <div className="section-animate text-center mb-12">
          <span className="font-accent text-autumn text-xl">Nos ambassadeurs</span>
          <h2 className="font-heading text-4xl sm:text-5xl font-bold text-forest mt-2 mb-6">
            Nos Chatons Adoptés
          </h2>
          <p className="font-body text-lg text-forest/70 max-w-2xl mx-auto">
            Découvrez les chatons qui ont trouvé leur famille pour toujours.
            Cliquez sur chaque fiche pour voir plus de photos et le témoignage de leur adoptant !
          </p>
        </div>

        {/* Gallery - scrollable */}
        <div className="section-animate">
          <ScrollArea className="w-full whitespace-nowrap pb-4">
            <div className="flex gap-6">
              {alumni.map((kitten) => (
                <AlumniCard 
                  key={kitten.id} 
                  kitten={kitten} 
                  onClick={() => setSelectedKitten(kitten)}
                />
              ))}
            </div>
            <ScrollBar orientation="horizontal" className="mt-4" />
          </ScrollArea>
        </div>

        {/* Stats */}
        <div className="section-animate mt-12 grid grid-cols-3 gap-4 max-w-md mx-auto">
          <div className="text-center">
            <p className="font-heading text-3xl font-bold text-forest">{alumni.length}</p>
            <p className="font-body text-sm text-forest/60">Chatons adoptés</p>
          </div>
          <div className="text-center border-x border-sand">
            <p className="font-heading text-3xl font-bold text-forest">100%</p>
            <p className="font-body text-sm text-forest/60">Familles heureuses</p>
          </div>
          <div className="text-center">
            <p className="font-heading text-3xl font-bold text-autumn">5★</p>
            <p className="font-body text-sm text-forest/60">Avis moyens</p>
          </div>
        </div>
      </div>

      {/* Modal */}
      <AlumniModal
        kitten={selectedKitten}
        isOpen={!!selectedKitten}
        onClose={() => setSelectedKitten(null)}
      />
    </section>
  );
}
